import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Users,
  Package,
  ShoppingCart,
  FolderTree,
  DollarSign,
  Truck,
  Shield,
  Languages,
  MessageSquare,
  Mail,
  Database,
  Settings,
  Settings2,
  Store,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  FileText,
  Upload,
  RefreshCw,
  Menu,
  X,
  Home,
  Wallet,
  UtensilsCrossed,
  Trash2,
  Activity,
  Calendar,
  Plug,
  Crown,
  Sparkles,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const NAV_SECTIONS = [
  {
    title: 'Genel',
    items: [
      { id: 'overview', label: 'Dashboard', icon: LayoutDashboard },
      { id: 'seller-verification', label: 'Satıcı Onayı', icon: Store },
    ]
  },
  {
    title: 'Yönetim',
    items: [
      { id: 'users', label: 'Kullanıcılar', icon: Users },
      { id: 'products', label: 'Ürünler', icon: Package },
      { id: 'ai-product', label: 'AI ile Ürün Yükle', icon: Sparkles },
      { id: 'orders', label: 'Siparişler', icon: ShoppingCart },
      { id: 'categories', label: 'Kategoriler', icon: FolderTree },
      { id: 'seller-uploads', label: 'Satıcı Yüklemeleri', icon: Upload },
      { id: 'bulk-delete-products', label: 'Toplu Ürün Sil', icon: Trash2 },
      { id: 'newsletter-subscriptions', label: 'Bülten Abonelikleri', icon: Mail },
      { id: 'membership-management', label: 'Üyelikler', icon: Crown },
      { id: 'finance-management', label: 'Finans Yönetimi', icon: Wallet },
    ]
  },
  {
    title: 'Operasyonlar',
    items: [
      { id: 'price-management', label: 'Fiyat Yönetimi', icon: DollarSign },
      { id: 'shipping-management', label: 'Kargo Yönetimi', icon: Truck },
      { id: 'bulk-category', label: 'Bayi Kategori Düzenleme', icon: Store },
      { id: 'reservations', label: 'Rezervasyon ve Formlar', icon: Calendar },
    ]
  },
  {
    title: 'Ana Sayfa',
    items: [
      { id: 'homepage-management', label: 'Ana Sayfa Yönetimi', icon: Home },
    ]
  },
  {
    title: 'İletişim',
    items: [
      { id: 'chat-management', label: 'Sohbet', icon: MessageSquare },
      { id: 'market-emails', label: 'Email Kampanya', icon: Mail },
      { id: 'restaurant-emails', label: 'Restoran E-posta', icon: UtensilsCrossed },
      { id: 'email-templates', label: 'Email Şablonları', icon: FileText },
    ]
  },
  {
    title: 'Sistem',
    items: [
      { id: 'system-logs', label: 'Loglar', icon: FileText },
      { id: 'mail-settings', label: 'Mail yönetimi', icon: Settings2 },
      { id: 'outbound-emails', label: 'Giden mailler', icon: Mail },
      { id: 'activity-logs', label: 'Etkinlik Günlüğü', icon: Activity },
      { id: 'translations', label: 'Çeviriler', icon: Languages },
      { id: 'role-management', label: 'Roller & Yetkiler', icon: Shield },
      { id: 'data-sync', label: 'Veri Senkron', icon: RefreshCw },
      { id: 'integrations', label: 'Entegrasyonlar', icon: Plug },
      { id: 'db-stats', label: 'Veritabanı', icon: Database },
      { id: 'ai-search', label: 'AI Arama', labelKey: 'search.aiAdminMenu', icon: Sparkles },
    ]
  }
];

const AdminSidebar = ({
  activeTab,
  onTabChange,
  collapsed,
  onToggleCollapse,
  hasPermission,
  pendingSellerApprovalCount = 0,
}) => {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleNavClick = (id) => {
    onTabChange(id);
    setMobileOpen(false);
  };

  const NavContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo/Header */}
      <div className={cn(
        "flex items-center h-16 px-4 border-b border-slate-800",
        collapsed ? "justify-center" : "justify-between"
      )}>
        {!collapsed && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white text-lg">Admin</span>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className="text-slate-400 hover:text-white hover:bg-slate-800 hidden lg:flex"
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </Button>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 py-4">
        <TooltipProvider delayDuration={0}>
          <nav className="space-y-6 px-3">
            {NAV_SECTIONS.map((section) => (
              <div key={section.title}>
                {!collapsed && (
                  <h3 className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {section.title}
                  </h3>
                )}
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.id;
                    
                    // Check permissions for certain items
                    if (item.id === 'system-logs' && !hasPermission('settings.roles')) return null;
                    if (item.id === 'activity-logs' && !hasPermission('settings.roles')) return null;
                    if (item.id === 'role-management' && !hasPermission('settings.roles')) return null;
                    if (item.id === 'price-management' && !hasPermission('products.edit')) return null;
                    if (item.id === 'shipping-management' && !hasPermission('products.edit')) return null;
                    if (item.id === 'translations' && !hasPermission('settings.roles')) return null;
                    if (item.id === 'db-stats' && !hasPermission('settings.roles')) return null;
                    if (item.id === 'mail-settings' && !hasPermission('settings.roles')) return null;
                    if (item.id === 'outbound-emails' && !hasPermission('settings.roles')) return null;
                    if (item.id === 'data-sync' && !hasPermission('settings.roles')) return null;
                    if (item.id === 'integrations' && !hasPermission('settings.roles')) return null;

                    const showSellerPending =
                      item.id === 'seller-verification' &&
                      hasPermission('sellers.view') &&
                      pendingSellerApprovalCount > 0;
                    const n =
                      pendingSellerApprovalCount > 99 ? '99+' : String(pendingSellerApprovalCount);

                    const NavButton = (
                      <button
                        type="button"
                        onClick={() => handleNavClick(item.id)}
                        className={cn(
                          'relative w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                          isActive
                            ? 'bg-red-600 text-white shadow-lg shadow-red-600/30'
                            : 'text-slate-400 hover:text-white hover:bg-slate-800/80',
                          collapsed && 'justify-center px-2',
                        )}
                        data-testid={`sidebar-${item.id}`}
                      >
                        <span className={cn('inline-flex shrink-0', collapsed && 'relative')}>
                          <Icon className={cn('w-5 h-5 flex-shrink-0', isActive && 'text-white')} />
                          {showSellerPending && collapsed && (
                            <span
                              className="absolute -top-2 -right-2 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-600 px-0.5 text-[9px] font-bold leading-none text-white shadow ring-2 ring-slate-900"
                              aria-label={`${pendingSellerApprovalCount} bekleyen başvuru`}
                            >
                              {n}
                            </span>
                          )}
                        </span>
                        {!collapsed && (
                          <>
                            <span
                              className={cn(
                                'flex-1 text-left',
                                showSellerPending && item.id === 'seller-verification' && 'pr-16',
                              )}
                            >
                              {item.labelKey ? t(item.labelKey, item.label) : item.label}
                            </span>
                            {item.badge && (
                              <span className="px-1.5 py-0.5 text-[10px] font-bold uppercase bg-amber-500 text-amber-950 rounded">
                                {item.badge}
                              </span>
                            )}
                            {showSellerPending && item.id === 'seller-verification' && (
                              <span
                                className="pointer-events-none absolute top-2 right-2 flex flex-col items-end gap-0.5"
                                aria-hidden
                              >
                                <span className="rounded bg-amber-500 px-1 py-[1px] text-[8px] font-extrabold uppercase leading-none text-amber-950">
                                  New
                                </span>
                                <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold tabular-nums text-white shadow">
                                  {n}
                                </span>
                              </span>
                            )}
                          </>
                        )}
                      </button>
                    );

                    if (collapsed) {
                      return (
                        <Tooltip key={item.id}>
                          <TooltipTrigger asChild>
                            {NavButton}
                          </TooltipTrigger>
                          <TooltipContent side="right" className="font-medium">
                            {item.labelKey ? t(item.labelKey, item.label) : item.label}
                            {showSellerPending
                              ? ` (${pendingSellerApprovalCount} bekleyen)`
                              : ''}
                          </TooltipContent>
                        </Tooltip>
                      );
                    }

                    return <div key={item.id}>{NavButton}</div>;
                  })}
                </div>
              </div>
            ))}
          </nav>
        </TooltipProvider>
      </ScrollArea>

      {/* Footer */}
      {!collapsed && (
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-800/50">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-red-600 flex items-center justify-center text-white text-sm font-bold">
              A
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">Admin</p>
              <p className="text-xs text-slate-500 truncate">Super Admin</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      <Button
        variant="outline"
        size="icon"
        onClick={() => setMobileOpen(true)}
        className="fixed top-4 left-4 z-50 lg:hidden bg-white shadow-lg"
        data-testid="mobile-menu-btn"
      >
        <Menu className="w-5 h-5" />
      </Button>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={cn(
          "fixed top-0 left-0 z-50 h-screen w-64 bg-slate-900 transform transition-transform duration-300 lg:hidden",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setMobileOpen(false)}
          className="absolute top-4 right-4 text-slate-400 hover:text-white"
        >
          <X className="w-5 h-5" />
        </Button>
        <NavContent />
      </aside>

      {/* Desktop Sidebar */}
      <aside
        className={cn(
          "hidden lg:flex flex-col h-screen bg-slate-900 border-r border-slate-800 transition-all duration-300 sticky top-0",
          collapsed ? "w-[72px]" : "w-64"
        )}
        data-testid="admin-sidebar"
      >
        <NavContent />
      </aside>
    </>
  );
};

export default AdminSidebar;
